import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Chip,
  Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authCodes, scratchCards } from '../services/api';

const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
  <Card>
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="div" color={color}>
        {value}
      </Typography>
      {subtitle && (
        <Typography color="textSecondary" variant="body2">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const LocationDisplay = ({ location, getCityFromCoordinates }) => {
  const [cityName, setCityName] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (location && location.latitude && location.longitude) {
      setLoading(true);
      getCityFromCoordinates(location.latitude, location.longitude)
        .then(setCityName)
        .finally(() => setLoading(false));
    }
  }, [location, getCityFromCoordinates]);

  if (!location) {
    return <Typography color="textSecondary">N/A</Typography>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={12} />
        <Typography color="textSecondary" variant="caption">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {cityName || `Location (${location.latitude?.toFixed(4)}, ${location.longitude?.toFixed(4)})`}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
        {location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)}
      </Typography>
    </Box>
  );
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    verificationStats: null,
    loyaltyStats: null,
    recentVerifications: [],
    productCodeStats: null
  });
  const [locationCache, setLocationCache] = useState({});
  
  // Pagination state for Recent Verifications
  const [verificationPage, setVerificationPage] = useState(1);
  const [verificationTotalPages, setVerificationTotalPages] = useState(1);
  const [verificationTotalCount, setVerificationTotalCount] = useState(0);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [allVerificationLogs, setAllVerificationLogs] = useState([]);


  // Function to get city name from coordinates using reverse geocoding
  const getCityFromCoordinates = async (latitude, longitude) => {
    const cacheKey = `${latitude},${longitude}`;
    
    // Check cache first
    if (locationCache[cacheKey]) {
      return locationCache[cacheKey];
    }


    try {
      // Using a free reverse geocoding service
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();
      
      let cityName = 'Unknown Location';
      
      // Try to get the most specific location information
      if (data.city) {
        cityName = data.city;
        // Add state/province if available
        if (data.principalSubdivision && data.principalSubdivision !== data.city) {
          cityName += `, ${data.principalSubdivision}`;
        }
        // Add country for context
        if (data.countryName) {
          cityName += `, ${data.countryName}`;
        }
      } else if (data.locality) {
        cityName = data.locality;
        if (data.principalSubdivision && data.principalSubdivision !== data.locality) {
          cityName += `, ${data.principalSubdivision}`;
        }
        if (data.countryName) {
          cityName += `, ${data.countryName}`;
        }
      } else if (data.principalSubdivision) {
        cityName = data.principalSubdivision;
        if (data.countryName) {
          cityName += `, ${data.countryName}`;
        }
      } else if (data.countryName) {
        cityName = data.countryName;
      }
      
      // If we only got country name or unknown, try a different service
      if (cityName === 'Unknown Location' || (data.countryName && !data.city && !data.locality && !data.principalSubdivision)) {
        try {
          // Try OpenStreetMap Nominatim as fallback
          const fallbackResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const fallbackData = await fallbackResponse.json();
          
          if (fallbackData.display_name) {
            const parts = fallbackData.display_name.split(', ');
            if (parts.length > 0) {
              cityName = parts[0]; // Get the first part (usually city/town)
              // Add additional parts for context (state, country)
              if (parts.length > 1) {
                cityName += `, ${parts[1]}`; // Add state/province
              }
              if (parts.length > 2) {
                cityName += `, ${parts[2]}`; // Add country
              }
            }
          }
        } catch (fallbackError) {
          console.warn('Fallback geocoding failed:', fallbackError);
          cityName = `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
        }
      }

      // Cache the result
      setLocationCache(prev => ({
        ...prev,
        [cacheKey]: cityName
      }));

      return cityName;
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      // Fallback to coordinates with better formatting
      return `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [productCode, scratchCardStats] = await Promise.all([
        authCodes.getAll(),
        scratchCards.getStats()
      ]);

      const productCodeStats = productCode.data.codes || productCode;

      setStats(prevStats => ({
        ...prevStats,
        loyaltyStats: scratchCardStats.data || scratchCardStats,
        productCodeStats: {
          total: productCodeStats.length,
          used: productCodeStats.filter(code => code.isUsed).length,
          available: productCodeStats.filter(code => !code.isUsed).length
        }
      }));

      // Fetch verification logs separately with pagination
      await fetchVerificationLogs(1);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationLogs = async () => {
    try {
      setVerificationLoading(true);
      // Fetch all verification logs (no pagination parameters)
      const verificationLogs = await authCodes.getVerificationLogs();

      const allLogs = verificationLogs.data.logs || verificationLogs.logs || [];
      const totalVerifications = allLogs.length;
      const usedProducts = allLogs.filter(log => log.isUsed).length;

      // Store all logs for frontend pagination
      setAllVerificationLogs(allLogs);

      // Calculate pagination for frontend
      const itemsPerPage = 5;
      const totalPages = Math.ceil(allLogs.length / itemsPerPage);
      const startIndex = (verificationPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedLogs = allLogs.slice(startIndex, endIndex);

      setStats(prevStats => ({
        ...prevStats,
        verificationStats: {
          total: totalVerifications,
          used: usedProducts,
          available: allLogs.length - usedProducts
        },
        recentVerifications: paginatedLogs
      }));

      // Set pagination info
      setVerificationTotalPages(totalPages);
      setVerificationTotalCount(allLogs.length);
    } catch (err) {
      console.error('Verification logs fetch error:', err);
      setError('Failed to fetch verification logs');
    } finally {
      setVerificationLoading(false);
    }
  };

  const applyVerificationPagination = () => {
    if (allVerificationLogs.length === 0) return;

    const itemsPerPage = 5;
    const startIndex = (verificationPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedLogs = allVerificationLogs.slice(startIndex, endIndex);

    setStats(prevStats => ({
      ...prevStats,
      recentVerifications: paginatedLogs
    }));
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Apply pagination when page changes
  useEffect(() => {
    applyVerificationPagination();
  }, [verificationPage, allVerificationLogs]);

  const handleVerificationPageChange = (event, page) => {
    setVerificationPage(page);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Product Verification Stats */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Product Verification
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Product Codes"
              value={stats.productCodeStats?.total || 0}
              subtitle="Generated codes"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Verified Products"
              value={stats.productCodeStats?.used || 0}
              subtitle="Successfully verified"
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Available Codes"
              value={stats.productCodeStats?.available || 0}
              subtitle="Not yet verified"
              color="info.main"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Loyalty Program Stats */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Loyalty Program
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Loyalty Cards"
              value={stats.loyaltyStats?.totalCards || 0}
              subtitle="Generated cards"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Redeemed Cards"
              value={stats.loyaltyStats?.redeemedCards || 0}
              subtitle={`${stats.loyaltyStats?.redemptionRate || '0%'} redemption rate`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Points Awarded"
              value={stats.loyaltyStats?.totalPointsAwarded || 0}
              subtitle="Points in circulation"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Verification Analytics */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Verification Analytics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Today's Verifications"
              value={stats.recentVerifications.filter(v => {
                const today = new Date();
                const verificationDate = new Date(v.verificationTime || v.timestamp);
                return verificationDate.toDateString() === today.toDateString();
              }).length}
              subtitle="Verified today"
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="This Week"
              value={stats.recentVerifications.filter(v => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                const verificationDate = new Date(v.verificationTime || v.timestamp);
                return verificationDate >= weekAgo;
              }).length}
              subtitle="Last 7 days"
              color="secondary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="With Location"
              value={stats.recentVerifications.filter(v => v.location).length}
              subtitle="GPS tracked"
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Verification Rate"
              value={`${stats.verificationStats?.total > 0 ? 
                Math.round((stats.verificationStats?.used / stats.verificationStats?.total) * 100) : 0}%`}
              subtitle="Codes verified"
              color="info.main"
            />
          </Grid>
        </Grid>
      </Box> */}

      {/* Recent Verifications */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Verifications
          </Typography>
          {verificationTotalCount > 0 && (
            <Typography variant="body2" color="textSecondary">
              Showing {((verificationPage - 1) * 5) + 1}-{Math.min(verificationPage * 5, verificationTotalCount)} of {verificationTotalCount} verifications
            </Typography>
          )}
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product Code</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Verified At</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verificationLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : stats.recentVerifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">
                      No verifications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stats.recentVerifications.map((verification) => (
                  <TableRow key={verification.id || verification.productCode}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {verification.authenticityCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={verification.regionName || verification.region?.name || 'Unknown'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {verification.verificationTime ? 
                          new Date(verification.verificationTime).toLocaleString() : 
                          new Date(verification.timestamp).toLocaleString()
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <LocationDisplay 
                        location={verification.location} 
                        getCityFromCoordinates={getCityFromCoordinates}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {verification.ipAddress || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {verificationTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={verificationTotalPages}
              page={verificationPage}
              onChange={handleVerificationPageChange}
              color="primary"
              disabled={verificationLoading}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DashboardPage;